import { useEffect, useState } from 'react';
import type { ToolLookupFilter } from '../../lib/api';
import {
  lookupFilterValueMatchesField,
  parseLookupFilterValue,
  type LookupSchemaField,
} from './lookup-tool-model';

interface LookupFilterValueControlProps {
  field: LookupSchemaField;
  value: ToolLookupFilter['value'];
  onChange: (value: ToolLookupFilter['value']) => void;
}

const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'True' },
  { value: 'false', label: 'False' },
];

const completeNumericValue = (field: LookupSchemaField, rawValue: string): number | null => {
  const pattern = field.type === 'integer' ? /^-?\d+$/ : /^-?(?:\d+|\d*\.\d+)(?:[eE][+-]?\d+)?$/;
  if (!pattern.test(rawValue.trim())) return null;

  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Renders the control a schema field's JSON type and enum metadata call for. */
export const LookupFilterValueControl = ({ field, value, onChange }: LookupFilterValueControlProps) => {
  const label = `${field.path} value`;
  const emitParsed = (rawValue: string) => onChange(parseLookupFilterValue(field, rawValue));
  const [numericText, setNumericText] = useState(String(value));
  const valueIsValid = lookupFilterValueMatchesField(field, value);

  // Keep the editing buffer aligned when the selected field or parent-owned value changes.
  useEffect(() => {
    setNumericText(String(value));
  }, [field.path, value]);

  if (field.enumValues?.length) {
    return (
      <select
        aria-label={label}
        aria-invalid={!valueIsValid}
        value={valueIsValid ? String(value) : ''}
        onChange={event => emitParsed(event.target.value)}>
        {!valueIsValid && <option value="">Choose a valid value…</option>}
        {field.enumValues.map(option => (
          <option key={String(option)} value={String(option)}>
            {String(option)}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'boolean') {
    return (
      <select
        aria-label={label}
        aria-invalid={!valueIsValid}
        value={valueIsValid ? String(value) : ''}
        onChange={event => emitParsed(event.target.value)}>
        {!valueIsValid && <option value="">Choose a valid value…</option>}
        {BOOLEAN_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  const isNumeric = field.type === 'integer' || field.type === 'number';

  if (isNumeric) {
    return (
      <input
        aria-label={label}
        aria-invalid={!valueIsValid}
        type="text"
        value={numericText}
        onChange={event => {
          const rawValue = event.target.value;
          setNumericText(rawValue);
          const parsed = completeNumericValue(field, rawValue);
          if (parsed !== null) onChange(parsed);
        }}
        onBlur={() => {
          const parsed = completeNumericValue(field, numericText);
          setNumericText(parsed === null ? String(value) : String(parsed));
          if (parsed !== null && !Object.is(parsed, value)) onChange(parsed);
        }}
      />
    );
  }

  return (
    <input
      aria-label={label}
      aria-invalid={!valueIsValid}
      type="text"
      value={String(value)}
      onChange={event => emitParsed(event.target.value)}
    />
  );
};
